import { useState } from "react";

import {
  googleEnabled,
  signIn,
  signInWithGoogle,
  takeGoogleSignInError,
} from "../auth";

/** Why a Google sign-in bounced, in words a client can act on.
 *
 * The backend redirects back with a short stable slug rather than an exception
 * message, so nothing from Google or the database reaches the URL. Mapping it
 * here is what turns that slug into something readable.
 */
const GOOGLE_ERRORS: Record<string, string> = {
  unknown_user:
    "We don't recognise that Google account yet. Please ask your iQuest contact to onboard you.",
  declined: "Google sign-in was cancelled.",
  session_expired: "Your session has ended. Please sign in again.",
  consumer_account:
    "That looks like a personal Google account. Please sign in with your work Google account.",
  domain_mismatch:
    "That Google account's domain doesn't match its email address. Please contact your iQuest contact.",
  no_domains_configured:
    "Your organisation's sign-in isn't finished being set up yet. Please ask your iQuest contact.",
  domain_not_permitted:
    "That email domain isn't permitted for your organisation. Please ask your iQuest contact.",
  ambiguous_domain:
    "Your email domain maps to more than one client, so we can't tell which is yours. Please ask your iQuest contact to onboard you manually.",
  inactive_user:
    "Your portal account isn't active. Please ask your iQuest contact.",
  state_expired: "That sign-in attempt timed out. Please try again.",
  exchange_failed: "We couldn't complete Google sign-in. Please try again.",
  no_subject: "Google did not return a usable account. Please try again.",
};

/* Provider marks are inlined as SVG rather than imported or fetched: the PWA
 * app shell caches this page, an external request would fail offline, and both
 * vendors' brand guidelines require their mark be reproduced unaltered, which
 * is easier to guarantee when the geometry is right here.
 *
 * Both carry aria-hidden: the button's own text is what a screen reader should
 * announce, and "Sign in with Microsoft" already says it.
 *
 * Sized 16 vs 18 deliberately. The Microsoft mark is four solid squares that
 * fill their box, while the Google G leaves internal whitespace, so equal boxes
 * make the G read smaller. These two are optically matched, not numerically. */

function MicrosoftMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

export function SignIn() {
  // WHICH provider is redirecting, not merely whether one is. A single
  // boolean put "Redirecting…" on the Microsoft button when Google was
  // clicked, which reads as though Google sign-in routes through Microsoft.
  const [busy, setBusy] = useState<"msal" | "google" | null>(null);
  // Read once on mount, not on every render: takeGoogleSignInError() clears the
  // stored reason as it reads, so calling it during render would lose the
  // message on the first re-render.
  const [googleError] = useState(() => takeGoogleSignInError());

  async function handleSignIn() {
    setBusy("msal");
    try {
      // iQuest staff and client users both authenticate through the external
      // multi-tenant app: the `organizations` authority accepts iQuest's own
      // tenant too, and the backend assigns roles from the portal_users row
      // (not the trust path), so a single button serves everyone. The internal
      // single-tenant MSAL instance is kept in msal.ts only so already-signed-in
      // staff sessions survive a deploy; it has no separate sign-in entry point.
      await signIn("external");
    } catch (err) {
      console.error(err);
      setBusy(null);
    }
  }

  return (
    <div className="signin-page">
      <div className="signin-card">
        <img src="/iquest-logo-light.svg" alt="iQuest" className="signin-logo" />
        <h1>iQuest Client Portal</h1>
        <p className="signin-blurb">
          {googleEnabled
            ? "Sign in with your work account to raise tickets, submit forms, and track requests."
            : "Sign in with your Microsoft work account to raise tickets, submit forms, and track requests."}
        </p>

        {googleError && (
          <p
            className="form-error"
            role="alert"
            style={{ marginBottom: "16px" }}
          >
            {GOOGLE_ERRORS[googleError] ??
              "Google sign-in didn't complete. Please try again."}
          </p>
        )}

        <button
          type="button"
          className="btn btn-primary"
          style={{ width: "100%" }}
          onClick={() => void handleSignIn()}
          disabled={busy !== null}
        >
          <MicrosoftMark />
          {busy === "msal" ? "Redirecting…" : "Sign in with Microsoft"}
        </button>

        {/* Two explicit buttons rather than email-first home-realm discovery.
            One client is Google-only today, so asking everyone to type an
            address before seeing a button would cost every other client a step
            to serve one. Revisit if more non-Microsoft clients arrive.

            Google is btn-ghost (outlined) and Microsoft is btn-primary, not
            because Google matters less but because equal visual weight on an
            identity choice invites wrong clicks: Microsoft is the path for
            every client but one. The outlined treatment also matches Google's
            own light button variant. */}
        {googleEnabled && (
          <button
            type="button"
            className="btn btn-ghost"
            style={{ width: "100%" }}
            onClick={() => {
              setBusy("google");
              signInWithGoogle();
            }}
            disabled={busy !== null}
          >
            <GoogleMark />
            {busy === "google" ? "Redirecting…" : "Sign in with Google"}
          </button>
        )}
      </div>
    </div>
  );
}
